/**
 * Figure S4's "✉ Shared with you · View · Helix Gene Vector · Alice A." chip.
 *
 * Round 1 of the whole-screen review found this chip carrying a document count
 * where the figure carries the PERMISSION and the OWNER. The recipient of a
 * share legitimately knows who shared their work and what they may do with it —
 * the project's owner already appears on the owners route the recipient can
 * call, and the recipient's own permission on the permissions route — so both
 * are real reads, not new exposure, and no backend change was needed:
 *
 *   GET /projects/{id}/owners                                → who shared it
 *     (returns the person's own `name` since build v2 round 2 — the pool held
 *     it all along, the route just never sent it, so the chip was printing the
 *     login handle "alice-inventor" where the figure shows a person's name)
 *   GET /permissions/effective?resource_id&grantee_id=<me>    → what you may do
 *
 * WHAT IS NEVER RENDERED: a raw user id, and anybody else's grant. The
 * permissions read is scoped to the caller's own grantee_id, so no other
 * person's row is ever fetched to fill this chip in. When the owners route
 * cannot name the owner, the chip says the share is there and that the owner is
 * not named — never a sub, never a guess.
 *
 * The chip stays "listed, not plotted": a share lets you read a project, it is
 * not consent to compute across it, and this project is still never on the map.
 */
import { useQuery } from '@tanstack/react-query'
import { getEffectivePermissions, getProjectOwners } from '../../services/api'
import { PERMISSION_WORD, sharerLabel } from './shareChipText'
import type { Project } from '../../types'

export interface SharedWithYouChipProps {
  project: Project
  /** The signed-in caller's own id — the grantee whose permission is read. */
  callerUserId?: string
}

export default function SharedWithYouChip({ project, callerUserId }: SharedWithYouChipProps) {
  const ownersQuery = useQuery({
    queryKey: ['project-owners', project.project_id],
    queryFn: () => getProjectOwners(project.project_id),
    retry: false,
  })
  const permissionQuery = useQuery({
    queryKey: ['effective-permissions', project.project_id, callerUserId],
    queryFn: () => getEffectivePermissions(project.project_id, callerUserId ?? ''),
    enabled: Boolean(callerUserId),
    retry: false,
  })

  const sharer = sharerLabel(ownersQuery.data?.[0])
  const level = permissionQuery.data?.permission_level
  const permission = level ? PERMISSION_WORD[level] ?? level : null

  return (
    <p
      data-part="SharedWithYouChip"
      title="Listed, not plotted — a share is not consent to compute across it"
      className="mt-1.5 rounded-md border border-dashed border-gray-300 bg-gray-50 px-2 py-1 text-[11px] text-gray-600"
    >
      ✉ Shared with you
      {permission ? <> · {permission}</> : null} · {project.name}
      {sharer ? <> · {sharer}</> : null}
      {!sharer && ownersQuery.isFetched ? <> · owner not named</> : null}
    </p>
  )
}
